import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Filter,
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { AppointmentQuickFind } from '../components/reception/AppointmentQuickFind';
import { useToast } from '../components/Toast';
import { AppointmentStatusBadge } from '../components/appointment/AppointmentStatusBadge';
import {
  APPOINTMENT_STATUS_OPTIONS,
  appointmentStatusLabel,
} from '../components/appointment/appointmentConsultationUtils';

interface AppointmentRow {
  id: string;
  scheduledAt: string;
  estimatedEndTime: string;
  status: string;
  type: string;
  clientFirstName?: string;
  clientLastName?: string;
  clientEmail?: string;
  shopName?: string;
  serviceName?: string;
  takenInChargeAt?: string | null;
  practitionerId?: string | null;
}

export function PractitionerAppointmentsListPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortKey, setSortKey] = useState('scheduledAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'scheduledAt' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortKey,
        sortDir,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
      });

      const resp = await apiClient(`/api/practitioner/appointments?${params.toString()}`);
      const data = await resp.json();
      if (!resp.ok || !data?.success) throw new Error(data?.message || 'Failed to fetch appointments');

      setAppointments(data.data.appointments || []);
      setTotalPages(data.data.totalPages || 1);
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, typeFilter, sortKey, sortDir, page]);

  return (
    <div className="p-6">
      <div className="dt-list-toolbar mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-textPrimary">Mes rendez-vous</h1>
          <p className="text-textSecondary mt-1">Gérez vos rendez-vous</p>
        </div>
        <button onClick={() => navigate('/practitioner/appointments/create')} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Nouveau
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Client, email ou code d’accueil…"
              className="input-field pl-10"
            />
          </div>
          <div className="md:col-span-1">
            <AppointmentQuickFind
              resolvePath={null}
              appointmentPath={(id) => `/practitioner/appointments/${id}`}
              onError={(message) => showToast(message, 'error')}
            />
          </div>
          <select className="input-field" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">Tous statuts</option>
            {APPOINTMENT_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {appointmentStatusLabel(status)}
              </option>
            ))}
          </select>
          <select className="input-field" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
            <option value="">Tous types</option>
            <option value="CONSULTATION">Consultation</option>
            <option value="TATTOO_SESSION">Tatouage</option>
            <option value="PIERCING">Piercing</option>
          </select>
          <button
            onClick={() => { setSearch(''); setStatusFilter(''); setTypeFilter(''); setPage(1); }}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('scheduledAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Date</span>
                    {sortKey === 'scheduledAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Statut</span>
                    {sortKey === 'status' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-500">Chargement…</td></tr>
              ) : appointments.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-500">Aucun rendez-vous</td></tr>
              ) : (
                appointments.map((a) => (
                  <tr key={a.id} className="group hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-gray-400" />
                        {a.scheduledAt ? new Date(a.scheduledAt).toLocaleString('fr-FR') : '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">{a.clientFirstName} {a.clientLastName}</div>
                      <div className="text-xs text-gray-500">{a.clientEmail || '—'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">{a.serviceName || '—'}</div>
                      <div className="text-xs text-gray-500">{a.shopName || '—'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <AppointmentStatusBadge status={a.status} />
                    </td>
                    <td className="px-6 py-4 text-right text-sm sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                      <div className="flex items-center justify-end gap-2">
                        <button className="btn-secondary px-3 py-1" onClick={() => navigate(`/practitioner/appointments/${a.id}`)}>Voir</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary px-3 py-1 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4 inline" /> Précédent
            </button>
            <span className="text-sm text-gray-700">Page {page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary px-3 py-1 disabled:opacity-50"
            >
              Suivant <ChevronRight className="w-4 h-4 inline" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PractitionerAppointmentsListPage;
